import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import axios from 'axios';
import { defaultErrorNotification } from '../../../../../../store/proApiSlice';
import { EVENT_ADMIN_LOGOUT_ACTION } from '../../../../../../AppConstants';
import { useDispatch } from 'react-redux';

export function statsJsonToCsv(
  jsonStats,
  start,
  end,
  isSuperAdmin = false,
  idMapping = {}
) {
  if (!jsonStats) return '';
  const headers = ['section', 'category', 'metric', 'value', 'trend'];

  const isMetric = (v) => v?.value !== undefined;
  let flattenedStats = [];

  for (const [section, sectionData = {}] of Object.entries(jsonStats)) {
    for (const [catKey, catValue = {}] of Object.entries(sectionData)) {
      // Case 1: Flat metric (e.g., (users -> total_users))
      if (isMetric(catValue)) {
        flattenedStats.push({
          section,
          category: 'all',
          metric: catKey, // e.g. total_users
          value: catValue.value,
          trend: catValue.trend ?? null,
        });
        continue;
      }

      // Case 2: Category with single-nested metrics (e.g., (bookings -> all) -> total_bookings)
      if (isMetric(Object.values(catValue)?.[0])) {
        for (const [metricKey, data] of Object.entries(catValue)) {
          flattenedStats.push({
            section,
            category: catKey, // all
            metric: metricKey, //  e.g. total_bookings
            value: data?.value ?? null,
            trend: data?.trend ?? null,
          });
        }
        continue;
      }

      // Case 3: Category with doubly-nested metrics (e.g., (bookings -> by_cat_id) -> id_1 -> total_bookings)
      for (const [catId, metrics = {}] of Object.entries(catValue)) {
        for (const [metricKey, data] of Object.entries(metrics)) {
          flattenedStats.push({
            section,
            category: idMapping?.[catId] || catId, // Lookup Category Name from ID
            metric: metricKey, // e.g. total_bookings
            value: data?.value ?? null,
            trend: data?.trend ?? null,
          });
        }
      }
    }
  }

  if (!isSuperAdmin) {
    flattenedStats = flattenedStats.filter(
      ({ section }) => !['advertisement', 'platform_users'].includes(section)
    );
  }

  const rows = flattenedStats.map((row) =>
    headers
      .map((field) => {
        const val = row[field] ?? '';
        const escaped = String(val).replace(/"/g, '""');
        return `"${escaped}"`;
      })
      .join(',')
  );

  return [
    'start_date,' + start,
    'end_date,' + end,
    '',
    headers.join(','),
    ...rows,
  ];
}

const CSV_EXTENSION = '.csv';

// Names can contain dots (e.g. "F.I Fishery"), so only a trailing ".csv" counts
// as the extension.
export function downloadCsv(filename, csvContent) {
  const name = filename?.trim() || 'export';
  const csvFilename = name.toLowerCase().endsWith(CSV_EXTENSION)
    ? name
    : `${name}${CSV_EXTENSION}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', csvFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const axiosCSVConfig = {
  withCredentials: true,
  baseURL: `${process.env.REACT_APP_EVENT_URL}`,
  responseType: 'blob',
  headers: {
    Accept: 'text/csv',
    'Content-Type': 'application/json',
  },
};

export function getFilenameFromDispositionHeader(headerValue) {
  if (!headerValue) return null;

  const match = headerValue.match(
    /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
  );

  if (!match) return null;

  // Decode in case it's URL-encoded (e.g. UTF-8'' form)
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function constructExportFilename(
  start,
  end,
  prefix = 'sb_export',
  extension = '.csv'
) {
  return `${prefix}__${start.format('YYYY-MM-DD')}__${end.format(
    'YYYY-MM-DD'
  )}${extension}`;
}

export function useDownloadAdminExportFileFromAPIQuery(url, prefix) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const makeRequest = useCallback(
    (start, end) => {
      setLoading(true);
      // Axios is used rather than RTK Query due to blob response type and browser caching behaviour
      axios
        .get(url, {
          ...axiosCSVConfig,
          params: {
            start: start.format('YYYY-MM-DD'),
            end: end.format('YYYY-MM-DD'),
            format: 'csv',
          },
        })
        .then((res) => {
          const filename = getFilenameFromDispositionHeader(
            res.headers?.['content-disposition']
          );
          downloadCsv(
            filename || constructExportFilename(start, end, prefix, '.csv'),
            res?.data
          );
        })
        .catch((err) => {
          defaultErrorNotification(err);
          if (
            err?.response?.status === 401 ||
            (err?.response?.status === 422 &&
              err?.response?.data.detail === 'Signature has expired')
          ) {
            dispatch({
              type: EVENT_ADMIN_LOGOUT_ACTION,
              trigger_logout: true,
            });
          }
        })
        .finally(() => setLoading(false));
    },
    [dispatch, prefix, url]
  );

  return [makeRequest, { loading }];
}

export function getStartDateForFilter(val) {
  if (val === 'yesterday') {
    return dayjs().subtract(1, 'day');
  } else if (val === 'week') {
    return dayjs().subtract(7, 'day');
  } else if (val === 'month') {
    // Month-to-date
    return dayjs().startOf('month');
  } else if (val === 'last_month') {
    return dayjs().subtract(30, 'day');
  } else if (val === 'all') {
    return dayjs(new Date(2020, 0, 1));
  }
  return dayjs();
}
